import ptechImage from '@/assets/promptech_logo.png';
import {
  AppBar,
  Box,
  Button,
  Container,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
  useScrollTrigger,
  Slide,
} from '@mui/material';
import styles from './topnav.module.css';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { fetchDefaultLanding, getCachedLanding, homePrimaryNav, LandingKey } from '@/config/landing';

interface NavProps {
  isNavHide?: boolean;
  /** overlay: 다크/이미지 배경용(기본, 투명+밝은 회색) · light: 화이트 배경(통합 메인)용 */
  variant?: 'overlay' | 'light';
}

interface HideOnScrollProps {
  children: React.ReactElement;
  window?: () => Window;
}

interface SubNavItem {
  title: string;
  url: string;
}
interface NavItem {
  title: string;
  url: string;
  submenu?: SubNavItem[];
}

const drawerWidth = 240;
const subNavMenuItems: SubNavItem[] = [
  { title: '주요연혁', url: '/histories' },
  { title: '특허', url: '/patents' },
];

const subNavProductItems: SubNavItem[] = [
  { title: 'Data Infuser', url: '/products/infuser' },
  { title: 'Data Map', url: '/products/datamap' },
  { title: 'Qufa Studio', url: '/products/qufa-studio' },
];

// 홈/공공데이터/AI 다음에 붙는 고정 메뉴(설정과 무관).
const fixedNavItems: NavItem[] = [
  { title: '제품소개', url: '', submenu: subNavProductItems },
  { title: '주요연혁', url: '', submenu: subNavMenuItems },
  { title: '회사소개', url: '/aboutus' },
];

const fixedMobileItems = [
  { title: '제품소개-Data Infuser', url: '/products/infuser' },
  { title: '제품소개-Data Map', url: '/products/datamap' },
  { title: '제품소개-Qufa Studio', url: '/products/qufa-studio' },
  { title: '주요연혁', url: '/histories' },
  { title: '특허', url: '/patents' },
  { title: '회사소개', url: '/aboutus' },
];

function HideOnScroll(props: HideOnScrollProps) {
  const { children, window } = props;
  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
  });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

export default function TopNav(props: NavProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [landing, setLanding] = useState<LandingKey>(getCachedLanding);
  const { pathname } = useLocation();

  // site-config.json 설정에 따라 홈/공공데이터/AI 메뉴 구성을 동적으로 결정한다.
  useEffect(() => {
    let active = true;
    fetchDefaultLanding().then((key) => {
      if (active) setLanding(key);
    });
    return () => {
      active = false;
    };
  }, []);

  const navItems: NavItem[] = [...homePrimaryNav(landing), ...fixedNavItems];
  const mobileNavItems: SubNavItem[] = [...homePrimaryNav(landing), ...fixedMobileItems];

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const handleMouseEnter = (index: number) => {
    setHoverIndex(index);
  };

  const handleMouseLeave = () => {
    setHoverIndex(null);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle}>
      <Box sx={{ my: 2, textAlign: 'center' }}>
        <Link href="/">
          <img src={ptechImage} height={30} alt="Promptech Logo" />
        </Link>
      </Box>
      <Divider />
      <List>
        {mobileNavItems.map((item: SubNavItem, index: number) => (
          <ListItem key={index} disablePadding>
            <ListItemButton component={Link} href={item.url} className={styles['drawer-item']}>
              <ListItemText primary={item.title} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const container = undefined;

  return (
    <Box sx={{ display: 'flex' }}>
      <HideOnScroll {...props}>
        <AppBar
          component="nav"
          className={`${styles['nav-header']}${props.variant === 'light' ? ' ' + styles['light'] : ''}`}
        >
          <Container maxWidth="xl">
            <Toolbar disableGutters>
              <IconButton
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                // light variant(흰 배경)에서는 어두운 색, overlay(다크 배경)에서는 밝은 색으로
                // 햄버거 아이콘 색을 명시한다(흰 배경에서 흰 아이콘이 안 보이던 문제 수정).
                sx={{ mr: 2, display: { sm: 'none' }, color: props.variant === 'light' ? '#181b24' : '#e8eaf1' }}
              >
                <FontAwesomeIcon icon={faBars} />
              </IconButton>
              <Box
                sx={{
                  flexGrow: 1,
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                <Link href="/">
                  <img src={ptechImage} height={30} alt="Promptech Logo" />
                </Link>
              </Box>

              <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: '30px' }}>
                {navItems.map((item: NavItem, index: number) => {
                  // 대메뉴 클릭 시: 자체 url 이 없으면 첫 번째 서브메뉴로 이동한다.
                  const targetUrl = item.url || item.submenu?.[0]?.url || '';
                  // 서브페이지에 있을 때도 부모 메뉴를 활성 표시한다.
                  const isActive =
                    (!!item.url && item.url === pathname) || !!item.submenu?.some((s) => s.url === pathname);
                  return (
                    <Box
                      key={index}
                      sx={{ display: 'inline-block', position: 'relative' }}
                      onMouseEnter={() => handleMouseEnter(index)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <Button
                        component={Link}
                        href={targetUrl}
                        className={`${styles['nav-link']} ${isActive ? styles['is-active'] : ''}`}
                      >
                        {item.title}
                      </Button>
                      {item.submenu && hoverIndex === index && (
                        <Box className={styles['submenu']}>
                          <div className={styles['submenu-panel']}>
                            {item.submenu.map((subItem: SubNavItem, subIndex: number) => (
                              <Link
                                href={subItem.url}
                                key={subIndex}
                                className={`${styles['submenu-item']} ${
                                  subItem.url === pathname ? styles['submenu-item-active'] : ''
                                }`}
                              >
                                {subItem.title}
                              </Link>
                            ))}
                          </div>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>
      <nav>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
    </Box>
  );
}
